import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Card, CardContent, Typography, Button, Chip, Slider,
  ToggleButton, ToggleButtonGroup, Radio, RadioGroup, FormControlLabel,
  Alert, CircularProgress, Divider, Stack, LinearProgress,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material'
import {
  CreditCard, AccountBalance, PhoneAndroid, CalendarMonth, Devices,
  AccountBalanceWallet, CheckCircle, Warning, Error as ErrorIcon,
} from '@mui/icons-material'
import {
  usePricingTiers, useSubscription, useCalculatePrice,
  useSubscribe, useUpgrade, useAvailableGateways,
  getDaysRemaining, useWallet, useWalletTransactions,
  type PriceCalculation, type WalletTransaction,
} from '../../hooks/useSubscription'
import { useDevices, type Device } from '../../hooks/useDevices'
import { useSearchParams } from 'react-router-dom'

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const o = d as { devices?: T[] } | undefined
  return o?.devices ?? []
}

const gatewayIcons: Record<string, typeof CreditCard> = {
  qi_card: CreditCard,
  zaincash: PhoneAndroid,
  hyperpay: CreditCard,
}

const statusConfig: Record<string, { color: string; bg: string; border: string; icon: typeof CheckCircle }> = {
  active:    { color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0', icon: CheckCircle },
  grace:     { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', icon: Warning },
  expired:   { color: '#ef4444', bg: '#fef2f2', border: '#fecaca', icon: ErrorIcon },
  cancelled: { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', icon: ErrorIcon },
}

export default function BillingPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const paymentStatus = searchParams.get('status') || searchParams.get('payment')

  const { data: tiers = [], isLoading: tiersLoading } = usePricingTiers()
  const { data: sub } = useSubscription()
  const { data: gateways = [] } = useAvailableGateways()
  const { data: devicesRaw } = useDevices()
  const devices = unwrap<Device>(devicesRaw)
  const currentDeviceCount = devices.length

  const { data: wallet } = useWallet()
  const { data: transactions = [] } = useWalletTransactions()

  const calculate = useCalculatePrice()
  const subscribe = useSubscribe()
  const upgrade = useUpgrade()

  const minDevices = Math.max(1, currentDeviceCount)
  const [deviceCount, setDeviceCount] = useState(sub?.device_count ?? minDevices)
  const [cycle, setCycle] = useState<string>(sub?.billing_cycle ?? 'monthly')
  const [selectedGateway, setSelectedGateway] = useState('')
  const [calc, setCalc] = useState<PriceCalculation | null>(null)

  useEffect(() => {
    analytics.track(EVENTS.BILLING_VIEWED)
  }, [])

  useEffect(() => {
    if (gateways.length > 0 && !selectedGateway) {
      setSelectedGateway(gateways[0].id)
    }
  }, [gateways])

  useEffect(() => {
    if (deviceCount < minDevices) setDeviceCount(minDevices)
  }, [minDevices])

  useEffect(() => {
    if (deviceCount > 0) {
      calculate.mutate({ device_count: deviceCount, billing_cycle: cycle }, {
        onSuccess: (data) => setCalc(data),
      })
    }
  }, [deviceCount, cycle])

  const isNewSubscription = !sub || sub.status === 'expired' || sub.status === 'cancelled'
  const isUpgrade = sub && sub.status === 'active' && (deviceCount > sub.device_count || cycle !== sub.billing_cycle)

  const handlePay = async () => {
    if (!selectedGateway || !calc) return
    const payload = { device_count: deviceCount, billing_cycle: cycle, gateway_id: selectedGateway }
    analytics.track(EVENTS.PAYMENT_INITIATED, { device_count: deviceCount, billing_cycle: cycle, gateway_id: selectedGateway, amount: calc.amount_due, is_upgrade: !!isUpgrade })
    try {
      const result = isUpgrade
        ? await upgrade.mutateAsync(payload)
        : await subscribe.mutateAsync(payload)

      if (result.redirect_url) {
        window.location.href = result.redirect_url
      } else if (result.checkout_id && result.script_url) {
        window.location.href = `/billing?gateway=hyperpay&checkoutId=${result.checkout_id}`
      }
    } catch {
      // error shown via mutation state
    }
  }

  const daysRemaining = getDaysRemaining(sub ?? null)
  const sc = statusConfig[sub?.status ?? ''] ?? statusConfig.expired

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em', mb: 0.5 }}>
        {t('billing.title')}
      </Typography>
      <Typography variant="body2" color="#94a3b8" sx={{ mb: 3 }}>
        {t('billing.subtitle')}
      </Typography>

      {paymentStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 2 }}>{t('billing.paymentSuccess')}</Alert>
      )}
      {paymentStatus === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>{t('billing.paymentFailed')}</Alert>
      )}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
        {/* Left: Subscription status + wallet + pricing tiers */}
        <Box sx={{ flex: 1 }}>

          {/* ── Plan Status Card ─────────────────────────── */}
          {sub ? (
            <Card sx={{ mb: 3, border: `1px solid ${sc.border}`, borderRadius: 2 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="#334155">
                    {t('billing.planStatus')}
                  </Typography>
                  <Chip
                    icon={<sc.icon sx={{ fontSize: 14 }} />}
                    label={sub.status.toUpperCase()}
                    size="small"
                    sx={{ bgcolor: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, fontWeight: 700, fontSize: 11 }}
                  />
                </Box>

                {/* KPI row */}
                <Stack direction="row" spacing={2} sx={{ mb: 2.5 }}>
                  {/* Days remaining */}
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Typography variant="caption" color="#64748b" fontWeight={600} sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('billing.remainingDays')}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} color={daysRemaining <= 7 ? '#ef4444' : daysRemaining <= 14 ? '#f59e0b' : '#10b981'} sx={{ lineHeight: 1.2, mt: 0.25 }}>
                      {sub.status === 'expired' ? t('billing.expired') : daysRemaining}
                    </Typography>
                    {sub.status !== 'expired' && (
                      <Typography variant="caption" color="#94a3b8">{t('billing.daysLeft', { days: daysRemaining })}</Typography>
                    )}
                  </Box>

                  {/* Device usage */}
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Typography variant="caption" color="#64748b" fontWeight={600} sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('billing.deviceUsage')}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} color={currentDeviceCount >= sub.device_count ? '#ef4444' : '#0f172a'} sx={{ lineHeight: 1.2, mt: 0.25 }}>
                      {currentDeviceCount}<Typography component="span" variant="h6" color="#94a3b8" fontWeight={600}>/{sub.device_count}</Typography>
                    </Typography>
                    <Typography variant="caption" color="#94a3b8">
                      {t('billing.devicesUsed', { used: currentDeviceCount, limit: sub.device_count })}
                    </Typography>
                  </Box>

                  {/* Cost */}
                  <Box sx={{ flex: 1, p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Typography variant="caption" color="#64748b" fontWeight={600} sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('billing.billingCycle')}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} color="#34A8E0" sx={{ lineHeight: 1.2, mt: 0.25 }}>
                      ${sub.total_amount.toFixed(0)}
                    </Typography>
                    <Typography variant="caption" color="#94a3b8">
                      {sub.billing_cycle === 'yearly' ? t('billing.annual') : t('billing.monthly')}
                    </Typography>
                  </Box>
                </Stack>

                {/* Progress bar */}
                {sub.status !== 'expired' && (
                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="#64748b">
                        {t('billing.startedAt')}: {new Date(sub.started_at).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="#64748b">
                        {t('billing.expiresAt')}: {new Date(sub.expires_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (daysRemaining / (sub.billing_cycle === 'yearly' ? 365 : 30)) * 100)}
                      sx={{
                        height: 8, borderRadius: 4,
                        bgcolor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: daysRemaining <= 7 ? '#ef4444' : daysRemaining <= 14 ? '#f59e0b' : '#34A8E0',
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Device usage bar */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="#64748b">{t('billing.deviceUsage')}</Typography>
                    <Typography variant="caption" fontWeight={600} color={currentDeviceCount >= sub.device_count ? '#ef4444' : '#64748b'}>
                      {Math.round((currentDeviceCount / sub.device_count) * 100)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (currentDeviceCount / sub.device_count) * 100)}
                    sx={{
                      height: 6, borderRadius: 3,
                      bgcolor: '#e2e8f0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        bgcolor: currentDeviceCount >= sub.device_count ? '#ef4444' : currentDeviceCount >= sub.device_count * 0.8 ? '#f59e0b' : '#10b981',
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>{t('billing.noSubscription')}</Alert>
          )}

          {/* ── Wallet Balance Card ──────────────────────── */}
          <Card sx={{ mb: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalanceWallet sx={{ color: '#34A8E0', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={700} color="#334155">
                    {t('billing.walletBalance')}
                  </Typography>
                </Box>
                {wallet && (
                  <Chip
                    label={wallet.currency}
                    size="small"
                    sx={{ bgcolor: '#EBF6FD', color: '#34A8E0', fontWeight: 600, fontSize: 11 }}
                  />
                )}
              </Box>

              {wallet ? (
                <Typography variant="h3" fontWeight={800} color={wallet.balance > 0 ? '#10b981' : '#94a3b8'} sx={{ letterSpacing: '-0.02em', mb: 0.5 }}>
                  {wallet.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  <Typography component="span" variant="h6" color="#94a3b8" fontWeight={500} sx={{ ml: 0.5 }}>
                    {wallet.currency}
                  </Typography>
                </Typography>
              ) : (
                <Typography variant="body2" color="#94a3b8">--</Typography>
              )}
            </CardContent>
          </Card>

          {/* ── Wallet Transactions ──────────────────────── */}
          <Card sx={{ mb: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} color="#334155">
                  {t('billing.recentTransactions')}
                </Typography>
                <Chip label={transactions.length} size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600, fontSize: 11 }} />
              </Box>

              {transactions.length === 0 ? (
                <Typography variant="body2" color="#94a3b8" sx={{ py: 2, textAlign: 'center' }}>
                  {t('billing.noTransactions')}
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { color: '#64748b', fontWeight: 600, fontSize: 11, borderColor: '#f1f5f9', bgcolor: '#f8fafc', textTransform: 'uppercase', letterSpacing: '0.04em', py: 1 } }}>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.slice(0, 20).map((tx: WalletTransaction) => {
                      const isCredit = tx.type === 'topup'
                      return (
                        <TableRow key={tx.id} sx={{ '& td': { borderColor: '#f1f5f9' }, '&:hover': { bgcolor: '#f8fafc' } }}>
                          <TableCell>
                            <Typography variant="caption" color="#64748b" fontFamily="monospace" fontSize={11}>
                              {new Date(tx.created_at).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tx.type === 'topup' ? t('billing.topup') : tx.type === 'subscription' ? t('billing.subscriptionPayment') : t('billing.deduction')}
                              size="small"
                              sx={{
                                fontSize: 10, fontWeight: 600,
                                bgcolor: isCredit ? '#f0fdf4' : '#fef2f2',
                                color: isCredit ? '#10b981' : '#ef4444',
                                border: `1px solid ${isCredit ? '#bbf7d0' : '#fecaca'}`,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} color={isCredit ? '#10b981' : '#ef4444'}>
                              {isCredit ? '+' : '-'}{Math.abs(tx.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="#475569" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {tx.description || '--'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* ── Pricing Tiers ────────────────────────────── */}
          <Card sx={{ mb: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#334155" sx={{ mb: 2 }}>
                {t('billing.pricingTiers')}
              </Typography>
              {tiersLoading ? <CircularProgress size={24} /> : (
                <Stack spacing={1}>
                  {tiers.map((tier) => (
                    <Box key={tier.id} sx={{
                      p: 1.5, borderRadius: 1.5, border: '1px solid #e2e8f0',
                      bgcolor: calc?.tier_name === tier.name ? '#f0fdf4' : '#fff',
                      transition: 'background 0.2s',
                    }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{tier.name}</Typography>
                          <Typography variant="caption" color="#94a3b8">
                            {tier.min_devices}–{tier.max_devices ?? '\u221E'} devices
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h6" fontWeight={700} color="#34A8E0">
                            ${tier.price_per_device_monthly}
                          </Typography>
                          <Typography variant="caption" color="#94a3b8">/device/month</Typography>
                          {tier.yearly_discount_pct > 0 && (
                            <Chip label={`${tier.yearly_discount_pct}% off yearly`} size="small"
                              sx={{ ml: 1, fontSize: 10, bgcolor: '#ede9fe', color: '#34A8E0' }} />
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Right: Calculator + gateway selection */}
        <Card sx={{ width: { xs: '100%', lg: 400 }, border: '1px solid #e2e8f0', alignSelf: 'flex-start', borderRadius: 2 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} color="#334155" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Devices fontSize="small" /> {t('billing.configurePlan')}
            </Typography>

            {/* Device count */}
            <Typography variant="body2" color="#64748b" sx={{ mb: 0.5 }}>
              {t('billing.numberOfDevices')}: <strong>{deviceCount}</strong>
              {currentDeviceCount > 0 && (
                <Typography component="span" variant="caption" color="#94a3b8"> (min: {currentDeviceCount} {t('billing.devices')})</Typography>
              )}
            </Typography>
            <Slider
              value={deviceCount}
              onChange={(_, v) => setDeviceCount(v as number)}
              min={minDevices}
              max={Math.max(200, minDevices + 100)}
              step={1}
              valueLabelDisplay="auto"
              sx={{ mb: 2, color: '#34A8E0' }}
            />

            {/* Billing cycle */}
            <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
              <CalendarMonth sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
              {t('billing.billingCycle')}
            </Typography>
            <ToggleButtonGroup
              value={cycle}
              exclusive
              onChange={(_, v) => { if (v) { setCycle(v); analytics.track(EVENTS.PLAN_SELECTED, { billing_cycle: v, device_count: deviceCount }) } }}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            >
              <ToggleButton value="monthly">{t('billing.monthly')}</ToggleButton>
              <ToggleButton value="yearly">
                Yearly
                {calc?.yearly_discount_pct ? ` (${calc.yearly_discount_pct}% off)` : ''}
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider sx={{ my: 2 }} />

            {/* Price summary */}
            {calc && (
              <Box sx={{ mb: 2 }}>
                <Stack spacing={0.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="#64748b">{t('billing.plan')}</Typography>
                    <Typography variant="body2" fontWeight={600}>{calc.tier_name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="#64748b">{t('billing.price')}</Typography>
                    <Typography variant="body2">${calc.price_per_device_monthly}/mo</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="#64748b">{t('billing.monthly')}</Typography>
                    <Typography variant="body2">${calc.monthly_total.toFixed(2)}</Typography>
                  </Box>
                  {calc.discount_amount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="#10b981">{t('billing.annual')}</Typography>
                      <Typography variant="body2" color="#10b981">-${calc.discount_amount.toFixed(2)}</Typography>
                    </Box>
                  )}
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" fontWeight={700}>{t('billing.usageSummary')}</Typography>
                    <Typography variant="h5" fontWeight={800} color="#34A8E0">
                      ${calc.amount_due.toFixed(2)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="#94a3b8" align="right">
                    {calc.billing_cycle === 'yearly' ? t('billing.annual') : t('billing.monthly')}
                  </Typography>
                </Stack>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Gateway selection */}
            <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
              <AccountBalance sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
              {t('billing.selectGateway')}
            </Typography>
            <RadioGroup value={selectedGateway} onChange={(e) => setSelectedGateway(e.target.value)}>
              {gateways.map((gw) => {
                const Icon = gatewayIcons[gw.provider] ?? CreditCard
                return (
                  <FormControlLabel
                    key={gw.id}
                    value={gw.id}
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Icon sx={{ fontSize: 18, color: '#64748b' }} />
                        <Typography variant="body2">{gw.name}</Typography>
                        <Chip label={gw.currency} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                      </Box>
                    }
                  />
                )
              })}
            </RadioGroup>

            {/* Action button */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handlePay}
              disabled={!selectedGateway || !calc || subscribe.isPending || upgrade.isPending}
              sx={{ mt: 2, background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', fontWeight: 700, py: 1.5, borderRadius: 1.5 }}
            >
              {subscribe.isPending || upgrade.isPending ? t('billing.processing') :
               isNewSubscription ? `${t('billing.subscribe')} — $${calc?.amount_due.toFixed(2) ?? '0'}` :
               isUpgrade ? `${t('billing.upgrade')} — $${calc?.amount_due.toFixed(2) ?? '0'}` :
               `${t('billing.renew30')} — $${calc?.amount_due.toFixed(2) ?? '0'}`}
            </Button>

            {(subscribe.isError || upgrade.isError) && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {(subscribe.error as any)?.response?.data?.error ??
                 (upgrade.error as any)?.response?.data?.error ?? 'Payment failed'}
              </Alert>
            )}

            {(subscribe.isSuccess || upgrade.isSuccess) && (
              <Alert severity="success" sx={{ mt: 1 }}>
                {t('billing.paymentSuccess')}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  )
}
